import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, formatDateTime } from '../../utils/api'
import '../../styles/pages.css'

function BrandDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [brand, setBrand] = useState(null)
  const [allBrands, setAllBrands] = useState([])

  // Form state
  const [formName, setFormName] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  // Reassign state
  const [targetBrandId, setTargetBrandId] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    loadData()
  }, [id])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [brandRes, brandsRes] = await Promise.all([
        api.get(`/api/pim/brands/${id}`),
        api.get('/api/pim/brands')
      ])

      setBrand(brandRes)
      setAllBrands(brandsRes.brands || [])
      setFormName(brandRes.name || '')
      setIsDirty(false)
    } catch (err) {
      console.error('Failed to load brand:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleNameChange(value) {
    setFormName(value)
    setIsDirty(value !== brand?.name)
    setSuccess(null)
  }

  async function handleSave(e) {
    e.preventDefault()
    if (!formName.trim()) {
      setError('Name is required')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await api.patch(`/api/pim/brands/${id}`, { name: formName.trim() })
      setBrand(response)
      setFormName(response.name)
      setIsDirty(false)
      setSuccess('Brand saved successfully')
    } catch (err) {
      console.error('Failed to save brand:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleReassign() {
    if (!targetBrandId) {
      setError('Please select a target brand')
      return
    }

    setReassigning(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await api.post(`/api/pim/brands/${id}/reassign`, {
        target_brand_id: parseInt(targetBrandId)
      })
      setSuccess(`${response.products_moved} product(s) moved successfully`)
      setTargetBrandId('')
      // Reload brand data
      await loadData()
    } catch (err) {
      console.error('Failed to reassign products:', err)
      setError(err.message)
    } finally {
      setReassigning(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    setError(null)

    try {
      await api.delete(`/api/pim/brands/${id}`)
      navigate('/pim/brands')
    } catch (err) {
      console.error('Failed to delete brand:', err)
      setError(err.message)
      setShowDeleteConfirm(false)
    } finally {
      setDeleting(false)
    }
  }

  function handleProductClick(productId) {
    navigate(`/pim/products/${productId}`)
  }

  // Filter out current brand from reassign options
  const reassignOptions = allBrands.filter(b => b.brand_id !== parseInt(id))

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!brand) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">Brand Not Found</div>
          <div className="empty-state-description">The requested brand could not be found.</div>
          <button className="btn btn-primary" onClick={() => navigate('/pim/brands')}>
            Back to Brands
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Back button */}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => navigate('/pim/brands')}
        style={{ marginBottom: 'var(--space-4)' }}
      >
        &larr; Back to Brands
      </button>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
        </div>
      )}

      {/* Brand Details Form */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ marginBottom: 'var(--space-4)' }}>Brand Details</h3>
        <form onSubmit={handleSave}>
          <div className="filter-group">
            <label className="filter-label">Brand Name *</label>
            <input
              type="text"
              className="form-input"
              value={formName}
              onChange={e => handleNameChange(e.target.value)}
              required
              style={{ maxWidth: 400 }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border)' }}>
            <div className="text-sm text-muted">
              Created: {formatDateTime(brand.created_at)} | Updated: {formatDateTime(brand.updated_at)}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isDirty || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Products Table */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h3>Products ({brand.master_product_count})</h3>

          {/* Reassign products */}
          {brand.products?.length > 0 && reassignOptions.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <select
                className="form-input"
                value={targetBrandId}
                onChange={e => setTargetBrandId(e.target.value)}
                style={{ width: 200 }}
              >
                <option value="">Move all to...</option>
                {reassignOptions.map(b => (
                  <option key={b.brand_id} value={b.brand_id}>
                    {b.name} ({b.master_product_count})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReassign}
                disabled={!targetBrandId || reassigning}
              >
                {reassigning ? 'Moving...' : 'Move'}
              </button>
            </div>
          )}
        </div>

        {brand.products?.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-description">No products linked to this brand.</div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Model</th>
                <th style={{ textAlign: 'center' }}>Variants</th>
              </tr>
            </thead>
            <tbody>
              {brand.products?.map(product => (
                <tr
                  key={product.master_product_id}
                  onClick={() => handleProductClick(product.master_product_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{ fontWeight: 500 }}>{product.model}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="badge badge-neutral">{product.variant_count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Section */}
      <div className="card" style={{ background: 'var(--color-bg-secondary)' }}>
        <h3 style={{ marginBottom: 'var(--space-3)', color: 'var(--color-error)' }}>Danger Zone</h3>
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-3)' }}>
          {brand.products?.length > 0
            ? `This brand has ${brand.master_product_count} product(s). Reassign or delete products first.`
            : 'This brand has no products and can be safely deleted.'
          }
        </p>

        {!showDeleteConfirm ? (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={brand.products?.length > 0}
          >
            Delete Brand
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <span className="text-sm" style={{ color: 'var(--color-error)' }}>
              Are you sure?
            </span>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default BrandDetail
